import { useEffect, useRef } from 'react';

import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { Button } from '@/components/ui/Button';
import { CheckCircle2 } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getMaxBridge } from '@/lib/max-bridge';

/**
 * Экран 11 — Подтверждение записи.
 *
 * Заголовок «Спасибо, {имя_родителя}!» — персонализация именем из contacts.
 * Текст из 3 параграфов (без slot-зависимостей — слот выбора удалён вместе
 * с CallTime в этом же PR; см. fix/remove-calltime-screen):
 *   1. «Заявка отправлена.»
 *   2. «Менеджер свяжется с вами в ближайшее время.»
 *   3. «Пробное занятие бесплатное.»
 * CTA «Закрыть» → bridge.close() (закрывает Mini App).
 *
 * При mount: помечаем сессию завершённой (markCompleted → stage='done' +
 * completedAt = Date.now()). При следующем открытии Mini App SessionContext
 * увидит stage='done' и поднимет M5 «Уже проходили». Резервный M5 trigger
 * через contacts !== null (Phase 3 stage 3) защищает от случая если родитель
 * не доходит до Confirmation.
 *
 * Аналитика:
 *  - confirmation_view (mount, без params).
 */

export default function Confirmation() {
  const { state, markCompleted } = useSession();
  const { track } = useAnalytics();

  // Track + markCompleted — один раз через ref-guard (защита от StrictMode).
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    track('confirmation_view');
    markCompleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parentName = state.contacts?.name ?? '';

  const handleClose = () => {
    try {
      getMaxBridge().close();
    } catch {
      // bridge может быть недоступен в тестах/браузере — игнор.
    }
  };

  return (
    <ScreenLayout
      cta={
        <Button onClick={handleClose} fullWidth>
          Закрыть
        </Button>
      }
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-5 py-8 text-center">
        <span
          aria-hidden="true"
          className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-success/15 text-success"
        >
          <CheckCircle2 size={48} strokeWidth={1.75} />
        </span>
        <h2 className="text-2xl font-bold leading-tight text-text-primary">
          Спасибо{parentName ? `, ${parentName}` : ''}!
        </h2>
        <div className="flex max-w-prose flex-col gap-3 text-base leading-relaxed text-text-secondary">
          <p>Заявка отправлена.</p>
          <p>Менеджер свяжется с вами в ближайшее время.</p>
          <p>Пробное занятие бесплатное.</p>
        </div>
      </div>
    </ScreenLayout>
  );
}
